/**
 * Keeping an eye on the other sessions, so the sidebar can say whether sharing would work.
 *
 * Presence — who is signed in — arrives from the server and is free. Neither of the two
 * things somebody actually wants to know does:
 *
 * **Is anybody there?** A socket stays open through a suspended laptop and a frozen tab
 * for as long as the network stack keeps it, so the server goes on listing a session that
 * has not run a line of JavaScript for hours. The only proof is asking it something and
 * being answered, which is what the `alive` exchange below is: two words relayed by the
 * server, which reads neither of them. It is answered from the socket's message handler
 * rather than a timer, deliberately — a backgrounded tab has its timers throttled to about
 * once a minute but still handles messages at once, so answering promptly is exactly what
 * separates "backgrounded" from "frozen".
 *
 * **Can it be reached from here?** That the server can see both sessions says nothing about
 * whether they can see each other; the files travel browser to browser and the server is
 * not on that path. The only honest test is to open a connection, so that is what is done:
 * the same negotiation a share makes, on a channel labelled `probe`, closed as soon as it
 * opens. Kept for a few minutes before being asked again, since the answer is a property
 * of two networks rather than of the moment.
 *
 * ## The two are checked in that order, and that is the point
 *
 * A probe against a sleeping machine fails, and would fail identically to a probe against a
 * machine on an unroutable network. Reporting the second when it is really the first would
 * light the indicator amber every night on any account with a headset on a shelf. So
 * nothing is probed until it has answered, and a session that has stopped answering is
 * reported as asleep and left alone — no probe, no fault, and only a slow ping to notice
 * when somebody comes back to it.
 */

import { describeHealth, type Health, type LinkState } from '../domain/health.js';
import type { PeerId } from '../domain/sync.js';
import { logger } from '../log.js';

const log = logger('share');

/** "Are you there?", relayed by the server, which does not read it. */
export const ALIVE = 'alive';
/** "Yes." */
export const ALIVE_ACK = 'alive-ack';

/** How often the state of everything is worked out again. */
export const TICK_MS = 5_000;

/** How long a session may go unheard from before it is asked whether it is still there. */
const PING_AFTER_MS = 10_000;

/**
 * Silence long enough to conclude that nobody is at it.
 *
 * Well clear of the minute a throttled background tab can take to get round to anything,
 * because the reply comes from the socket rather than a timer and so is not throttled at
 * all — this only has to outlast a slow network, not a slow tab.
 */
export const DORMANT_AFTER_MS = 45_000;

/** How often to prod one that has gone quiet, which is only to notice it waking. */
const DORMANT_PING_MS = 60_000;

/**
 * How long a verdict about reaching a session is believed.
 *
 * Long, because a full negotiation is not free and the answer rarely changes: it is a fact
 * about two networks. Short enough that moving a laptop from the office to home is noticed
 * without pressing anything.
 */
export const PROBE_FRESH_MS = 5 * 60_000;

export interface WatchOptions {
  /** Ask a session whether it is there. Sent through the server's relay. */
  readonly ping: (peer: PeerId) => void;
  /** Open a connection to a session and close it again. Rejects if no route exists. */
  readonly probe: (peer: PeerId) => Promise<void>;
  /** Called whenever the answer changes, and only then. */
  readonly onChange: (health: Health) => void;
  /** The clock, so the rules above can be tested without waiting for them. */
  readonly now: () => number;
}

interface Watched {
  /** When this session was first seen, so a new arrival is not immediately written off. */
  readonly since: number;
  /** When it last said anything. */
  heard: number | undefined;
  /** When it was last asked, so a quiet one is not asked once per tick. */
  asked: number | undefined;
  /** The last verdict on reaching it, if there has been one. */
  reachable: boolean | undefined;
  /** When that verdict was reached. */
  checkedAt: number | undefined;
  /** True while a probe is in flight, which takes seconds and must not be started twice. */
  probing: boolean;
}

export class PeerWatch {
  private readonly watched = new Map<PeerId, Watched>();
  private connected = false;
  private busy = false;
  private timer: ReturnType<typeof setInterval> | undefined;
  private last: Health | undefined;

  private readonly wake = (): void => {
    this.tick();
  };

  constructor(private readonly options: WatchOptions) {}

  /** Begin watching. Signing out stops it; there is nothing to watch without an account. */
  start(): void {
    if (this.timer !== undefined) return;

    this.timer = setInterval(() => {
      this.tick();
    }, TICK_MS);

    // Coming back to a tab that has been in the background is the moment somebody looks at
    // this, and is also the moment its timers have been throttled to about once a minute.
    // Without this, the indicator would be up to a minute stale exactly when it is read.
    document.addEventListener('visibilitychange', this.wake);

    this.tick();
  }

  stop(): void {
    // Both are put in place together, so one standing in for the other is safe — and
    // stopping something that was never started must not reach for a `document`.
    if (this.timer !== undefined) {
      clearInterval(this.timer);
      this.timer = undefined;
      document.removeEventListener('visibilitychange', this.wake);
    }

    this.watched.clear();
    this.connected = false;
    // Not published: signing out is not a fault to report, and the indicator goes with the
    // button. Forgetting the last answer instead, so that signing in again is reported
    // afresh rather than compared against whatever was true before.
    this.last = undefined;
  }

  /** Whether the socket to the server is up. Without it, nothing else is knowable. */
  setConnected(connected: boolean): void {
    this.connected = connected;
    if (!connected) this.watched.clear();
    this.publish();
  }

  /** Everybody the server says is there, replacing whoever was there before. */
  setPeers(peers: readonly PeerId[]): void {
    const present = new Set(peers);
    for (const peer of [...this.watched.keys()]) {
      if (!present.has(peer)) this.watched.delete(peer);
    }
    for (const peer of peers) this.watch(peer);
    this.tick();
  }

  add(peer: PeerId): void {
    this.watch(peer);
    // Rather than up to a tick later: an arrival is the one moment somebody is likely to
    // be watching this, having just signed in on the other machine.
    this.tick();
  }

  remove(peer: PeerId): void {
    if (this.watched.delete(peer)) this.publish();
  }

  /**
   * A session said something, so it is running.
   *
   * Anything from it counts, not only an answer to a ping: a session that opened a
   * connection to us has proved rather more than a reply would.
   */
  heard(peer: PeerId): void {
    const watched = this.watched.get(peer);
    if (!watched) return;

    const now = this.options.now();
    // Waking up can mean waking up somewhere else — a laptop opened at home rather than at
    // the office — so a verdict reached before it went quiet is not one worth keeping.
    if (!awake(watched, now)) watched.checkedAt = undefined;

    watched.heard = now;
    this.publish();
  }

  /** Set down what a connection attempt found, whether from a probe or from a real share. */
  record(peer: PeerId, reachable: boolean): void {
    const watched = this.watched.get(peer);
    if (!watched) return;

    const now = this.options.now();
    watched.reachable = reachable;
    watched.checkedAt = now;
    // A connection that opened says more about the other end being awake than any ping.
    if (reachable) watched.heard = now;

    this.publish();
  }

  /**
   * Hold off while a share is running.
   *
   * Not because a probe would break one — they are separate connections and the code
   * already handles two at once — but because a share is several seconds of somebody
   * waiting, and spending any of it negotiating a connection whose only purpose is to be
   * closed again is a poor trade. The share reports what it finds anyway.
   */
  setBusy(busy: boolean): void {
    this.busy = busy;
  }

  get health(): Health {
    return describeHealth(this.connected, this.states());
  }

  /** Work out what is known, ask about what is not, and say so if the answer changed. */
  tick(): void {
    if (this.connected) {
      const now = this.options.now();
      for (const [peer, watched] of this.watched) this.attend(peer, watched, now);
    }
    this.publish();
  }

  private attend(peer: PeerId, watched: Watched, now: number): void {
    const state = stateOf(watched, now);

    // Asked at a distance from the last question rather than the last answer, so that a
    // session which never replies is not asked once per tick for ever.
    const gap = state === 'dormant' ? DORMANT_PING_MS : PING_AFTER_MS;
    const quiet = watched.heard === undefined || now - watched.heard >= PING_AFTER_MS;
    const patient = watched.asked === undefined || now - watched.asked >= gap;
    if (quiet && patient) {
      watched.asked = now;
      this.options.ping(peer);
    }

    if (this.busy || watched.probing) return;
    // The whole of the sleeping-laptop answer: nothing is probed until it has spoken.
    if (!awake(watched, now)) return;
    if (watched.checkedAt !== undefined && now - watched.checkedAt < PROBE_FRESH_MS)
      return;

    this.check(peer, watched);
  }

  private check(peer: PeerId, watched: Watched): void {
    watched.probing = true;

    void this.options
      .probe(peer)
      .then(() => {
        this.record(peer, true);
      })
      .catch((error: unknown) => {
        log.debug(`No route to session ${peer} was found.`, error);
        this.record(peer, false);
      })
      .finally(() => {
        watched.probing = false;
        this.publish();
      });
  }

  private watch(peer: PeerId): void {
    if (this.watched.has(peer)) return;
    this.watched.set(peer, {
      since: this.options.now(),
      heard: undefined,
      asked: undefined,
      reachable: undefined,
      checkedAt: undefined,
      probing: false,
    });
  }

  private states(): LinkState[] {
    const now = this.options.now();
    return [...this.watched.values()].map((watched) => stateOf(watched, now));
  }

  private publish(): void {
    const health = this.health;
    // Only when it has actually changed: this runs every few seconds, and the line has
    // `role="status"`, so republishing the same words would have a screen reader announce
    // them over and over.
    if (this.last?.level === health.level && this.last.summary === health.summary) return;

    this.last = health;
    this.options.onChange(health);
  }
}

function awake(watched: Watched, now: number): boolean {
  return watched.heard !== undefined && now - watched.heard < DORMANT_AFTER_MS;
}

function stateOf(watched: Watched, now: number): LinkState {
  if (!awake(watched, now)) {
    // One that has never said anything and has only just arrived is still arriving. One
    // that has been silent for longer than that has either gone to sleep or never woken.
    return watched.heard === undefined && now - watched.since < DORMANT_AFTER_MS
      ? 'unknown'
      : 'dormant';
  }

  if (watched.reachable === undefined) return 'unknown';
  return watched.reachable ? 'reachable' : 'unreachable';
}

/** Read a liveness message, if that is what this is. */
export function readAlive(payload: unknown): typeof ALIVE | typeof ALIVE_ACK | undefined {
  if (typeof payload !== 'object' || payload === null) return undefined;

  const kind = (payload as Record<string, unknown>).kind;
  if (kind === ALIVE) return ALIVE;
  if (kind === ALIVE_ACK) return ALIVE_ACK;
  return undefined;
}
